import React from 'react';
import type { CareSymbolId } from '../pieces';

// ISO 3758-style laundry symbols, drawn on a 24×24 grid with the text colour.
const TUB = (
  <>
    <path d="M2.5 8.5 4.9 19.5h14.2l2.4-11" />
    <path d="M2.5 8.5c1.6-2.2 3.2 1.4 4.8-.6s3.2 1.4 4.8-.6 3.2 1.4 4.8-.6 2.6 1.1 4.6-.2" />
  </>
);
const SHORT_TUB = (
  <>
    <path d="M2.5 7 4.7 16.6h14.6L21.5 7" />
    <path d="M2.5 7c1.6-2.2 3.2 1.4 4.8-.6s3.2 1.4 4.8-.6 3.2 1.4 4.8-.6 2.6 1.1 4.6-.2" />
  </>
);
const SQUARE = <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" />;
const SHADE = <path d="M3.8 11 9 5.8M3.8 14.6l8.6-8.6" />;
const IRON = (
  <>
    <path d="M2.5 18.5h19v-5.6c0-2.2-1.4-3.6-3.6-3.6H9.4c-3.9 0-6.4 3.2-6.9 9.2z" />
    <path d="M9.6 9.3V6.6h9.2" />
  </>
);
const dot = (cx: number, cy: number) => <circle cx={cx} cy={cy} r="1.05" fill="currentColor" stroke="none" />;
const CROSS = <path d="m4 4 16 16M20 4 4 20" />;
const temp = (t: string) => (
  <text x="12" y="17.2" textAnchor="middle" fontSize="6.6" fontWeight="600" fill="currentColor" stroke="none">
    {t}
  </text>
);

const SHAPES: Record<CareSymbolId, React.ReactNode> = {
  wash: TUB,
  w30: <>{TUB}{temp('30')}</>,
  w40: <>{TUB}{temp('40')}</>,
  w60: <>{TUB}{temp('60')}</>,
  wcold: <>{TUB}{dot(12, 14.5)}</>,
  wgentle: <>{SHORT_TUB}{dot(12, 12.4)}<path d="M4.5 20.5h15" /></>,
  whand: <>{TUB}<path d="M9 17.5v-4.2a1 1 0 0 1 2 0v2.4-3.6a1 1 0 0 1 2 0v3.4-2.6a1 1 0 0 1 2 0v4.6" /></>,
  nobleach: <><path d="M12 3.5 21.5 19.5h-19z" /><path d="m5 5 14 16M19 5 5 21" /></>,
  tumble: <>{SQUARE}<circle cx="12" cy="12" r="6" /></>,
  notumble: <>{SQUARE}<circle cx="12" cy="12" r="6" />{CROSS}</>,
  line: <>{SQUARE}<path d="M3.8 6.2c5.5 4.6 10.9 4.6 16.4 0" /></>,
  lineshade: <>{SQUARE}<path d="M3.8 6.2c5.5 4.6 10.9 4.6 16.4 0" />{SHADE}</>,
  flat: <>{SQUARE}<path d="M7 12h10" /></>,
  flatshade: <>{SQUARE}<path d="M7 12h10" />{SHADE}</>,
  iron: IRON,
  iron1: <>{IRON}{dot(13.4, 14.3)}</>,
  iron2: <>{IRON}{dot(11.4, 14.3)}{dot(15.4, 14.3)}</>,
  iron3: <>{IRON}{dot(9.6, 14.3)}{dot(13.4, 14.3)}{dot(17.2, 14.3)}</>,
  noiron: <>{IRON}{CROSS}</>,
  dryclean: <circle cx="12" cy="12" r="8.5" />,
  nodryclean: <><circle cx="12" cy="12" r="8.5" />{CROSS}</>,
};

export const CARE_LABELS: Record<CareSymbolId, string> = {
  wash: 'Machine wash', w30: 'Wash at 30°', w40: 'Wash at 40°', w60: 'Wash at 60°', wcold: 'Cold wash',
  wgentle: 'Cold, gentle wash', whand: 'Hand wash', nobleach: 'No bleach', tumble: 'Tumble dry',
  notumble: 'No tumble dryer', line: 'Line dry', lineshade: 'Line dry in shade', flat: 'Dry flat',
  flatshade: 'Dry flat in shade', iron: 'Iron', iron1: 'Cool iron', iron2: 'Warm iron', iron3: 'Hot iron',
  noiron: 'Do not iron', dryclean: 'Dry clean', nodryclean: 'Do not dry clean',
};

const CareSymbol: React.FC<{ id: CareSymbolId; size?: number }> = ({ id, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.35}
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={CARE_LABELS[id]}
  >
    <title>{CARE_LABELS[id]}</title>
    {SHAPES[id]}
  </svg>
);

export default CareSymbol;
